import { useEffect, useLayoutEffect, useState } from 'react'
import { useModalState } from '~/atoms/ModalState'

/**
 *
 * @param {HTMLElement | null} targetElement - The target element to observe for visibility.
 * @param {number} [retryDelay=100] - Some elements have animations that delay the rendering of UI elements. `retryDelay` allows for the animation to complete before checking for visibility.
 * @returns {boolean} - Returns true if the target element is visible in both the window and not obstructed by a modal.
 */
export const useIsInActiveView = (
  targetElement?: HTMLElement | null,
  retryDelay: number = 100,
) => {
  const [, setDidRetry] = useState(false)
  const [isVisibleWindow, setIsVisibleWindow] = useState(false)
  const [isVisibleModal, setIsVisibleModal] = useState(true)
  const { activeModalRef } = useModalState()

  useLayoutEffect(() => {
    if (!targetElement) return
    const windowObserver = new IntersectionObserver(
      ([entry]) => {
        setIsVisibleWindow(entry.isIntersecting)
      },
      { threshold: 1 },
    )

    windowObserver.observe(targetElement)

    return () => {
      windowObserver.unobserve(targetElement)
    }
  })

  useLayoutEffect(() => {
    if (!targetElement || !activeModalRef) {
      setIsVisibleModal(true)
      return
    }

    const modalObserver = new IntersectionObserver(
      ([entry]) => {
        setIsVisibleModal(entry.isIntersecting)
      },
      {
        root: activeModalRef,
        threshold: 0.1,
      },
    )

    modalObserver?.observe(targetElement)

    return () => {
      modalObserver?.unobserve(targetElement)
    }
  }, [activeModalRef])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDidRetry(true)
    }, retryDelay)

    return () => {
      clearTimeout(timeout)
    }
  }, [retryDelay])

  return isVisibleWindow && isVisibleModal
}
